/**
 * TASK-829 — React Query hooks over the CIO read surface.
 *
 * `staleTime` is generous on purpose: the house view is a periodically-imported snapshot
 * (live: one vintage, unchanged since May), not live market data. Refetching it aggressively
 * would spend requests to re-learn a date.
 */

import { useQuery } from '@tanstack/react-query';
import { cioService } from '../services/cioService';

const SNAPSHOT_STALE_MS = 10 * 60 * 1000;

export function useCioVintage() {
  return useQuery({
    queryKey: ['cio-vintage'],
    queryFn: () => cioService.getVintage(),
    staleTime: SNAPSHOT_STALE_MS,
  });
}

export function useCioHoldings(clientId: string) {
  return useQuery({
    queryKey: ['cio-holdings', clientId],
    queryFn: () => cioService.getHoldings(clientId),
    enabled: !!clientId,
    staleTime: SNAPSHOT_STALE_MS,
  });
}

export function useCioCandidates(params: { sac?: string; ig?: string; clientId?: string }) {
  return useQuery({
    queryKey: ['cio-candidates', params.sac ?? '', params.ig ?? '', params.clientId ?? ''],
    queryFn: () => cioService.getCandidates(params),
    // A bucket query with no bucket would return the whole universe — the caller must say
    // which slice it wants.
    enabled: Boolean(params.sac || params.ig),
    staleTime: SNAPSHOT_STALE_MS,
  });
}
